/**
 * Support Page
 *
 * Authenticated support interface at /dashboard/help/support.
 * Provides full access to account-specific support with escalation.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import {
  MessageCircle,
  Send,
  Bot,
  User,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";
import { useAuth, useUser } from "@clerk/clerk-react";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface SupportTicket {
  _id: string;
  threadId: string;
  status: string;
  summary?: string;
  replyContent?: string;
  repliedAt?: number;
  createdAt: number;
}

// Generate user-specific storage keys to isolate chat data per user
const getStorageKey = (userId: string | undefined) =>
  userId ? `manageclean_support_thread_${userId}` : null;
const getThreadIdKey = (userId: string | undefined) =>
  userId ? `support_threadId_${userId}` : null;

export function SupportPage() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const userId = user?.id;

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // User-specific storage keys
  const storageKey = useMemo(() => getStorageKey(userId), [userId]);
  const threadIdKey = useMemo(() => getThreadIdKey(userId), [userId]);

  // Get user's tickets
  const tickets = useQuery(api.supportTickets.listUserTickets) ?? [];

  // Load thread from localStorage (user-specific)
  useEffect(() => {
    if (!storageKey) return; // Wait for user ID

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setMessages(
          parsed.messages.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          }))
        );
      } catch (e) {
        console.error("Failed to parse saved support thread:", e);
      }
    } else {
      // Welcome message
      setMessages([
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            "Hi! 👋 I'm your ManageClean OS support assistant. I have access to your account and can help with billing, subscriptions, technical issues, and more. How can I help you today?",
          timestamp: new Date(),
        },
      ]);
    }
  }, [storageKey]);

  // Save thread to localStorage (user-specific)
  useEffect(() => {
    if (!storageKey) return; // Wait for user ID
    if (messages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify({ messages }));
    }
  }, [messages, storageKey]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const [threadId, setThreadId] = useState<string | undefined>(undefined);

  // Load threadId from user-specific localStorage
  useEffect(() => {
    if (!threadIdKey) return;
    const savedThreadId = localStorage.getItem(threadIdKey);
    if (savedThreadId) {
      setThreadId(savedThreadId);
    }
  }, [threadIdKey]);

  const supportChat = useAction(api.agents.supportAgent.supportChat);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading || !threadIdKey) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const result = await supportChat({
        message: userMessage.content,
        threadId: threadId,
      });

      if (result.threadId) {
        setThreadId(result.threadId);
        localStorage.setItem(threadIdKey, result.threadId);
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: result.response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Support chat error:", error);
      const errorMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "I apologize, but I'm having trouble responding right now. Please try again in a moment.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, threadId, threadIdKey, supportChat]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleNewChat = useCallback(() => {
    setMessages([
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content:
          "Hi! 👋 I'm your ManageClean OS support assistant. I have access to your account and can help with billing, subscriptions, technical issues, and more. How can I help you today?",
        timestamp: new Date(),
      },
    ]);
    if (storageKey) localStorage.removeItem(storageKey);
    if (threadIdKey) localStorage.removeItem(threadIdKey);
    setThreadId(undefined);
  }, [storageKey, threadIdKey]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "resolved":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "replied":
        return <MessageCircle className="h-4 w-4 text-blue-500" />;
      case "escalated":
      case "human_needed":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "resolved":
        return (
          <Badge variant="outline" className="border-green-200 bg-green-50 text-green-600">
            Resolved
          </Badge>
        );
      case "replied":
        return (
          <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-600">
            Replied
          </Badge>
        );
      case "escalated":
      case "human_needed":
        return (
          <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-600">
            Escalated
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-600">
            In Progress
          </Badge>
        );
    }
  };

  if (!isSignedIn) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <HelpCircle className="mx-auto mb-4 h-12 w-12 text-blue-500" />
            <h2 className="mb-2 text-xl font-semibold">Sign in Required</h2>
            <p className="mb-4 text-gray-600">
              Please sign in to access support and get help with your account.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout activeTab="help">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Support</h1>
        <p className="text-gray-600">Get help with your account, billing, and technical issues</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chat Interface */}
        <div className="lg:col-span-2">
          <Card className="flex h-[calc(100vh-220px)] flex-col">
            {/* Header */}
            <CardHeader className="rounded-t-lg border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-white/20 p-2">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Support Assistant</CardTitle>
                    <p className="text-sm text-blue-100">Account access enabled</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={handleNewChat}
                >
                  New Chat
                </Button>
              </div>
            </CardHeader>

            {/* Messages */}
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full p-4">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex gap-3",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {message.role === "assistant" && (
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                          <Bot className="h-4 w-4 text-blue-600" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[75%] rounded-2xl px-4 py-3 text-sm",
                          message.role === "user"
                            ? "rounded-br-md bg-blue-600 text-white"
                            : "rounded-bl-md bg-gray-100 text-gray-800"
                        )}
                      >
                        <div
                          className={
                            message.role === "assistant"
                              ? "prose prose-sm max-w-none"
                              : "whitespace-pre-wrap"
                          }
                        >
                          {message.role === "assistant" ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {message.content}
                            </ReactMarkdown>
                          ) : (
                            message.content
                          )}
                        </div>
                        <div
                          className={cn(
                            "mt-1 text-xs",
                            message.role === "user" ? "text-blue-200" : "text-gray-400"
                          )}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                      {message.role === "user" && (
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                          <User className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <Bot className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          height={16}
                          viewBox="0 0 24 24"
                          width={16}
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-blue-600"
                        >
                          <title>Loading...</title>
                          <style>{`
                                                        .spinner-bar {
                                                            animation: spinner-bars-animation .8s linear infinite;
                                                            animation-delay: -.8s;
                                                        }
                                                        .spinner-bars-2 {
                                                            animation-delay: -.65s;
                                                        }
                                                        .spinner-bars-3 {
                                                            animation-delay: -0.5s;
                                                        }
                                                        @keyframes spinner-bars-animation {
                                                            0% {
                                                                y: 1px;
                                                                height: 22px;
                                                            }
                                                            93.75% {
                                                                y: 5px;
                                                                height: 14px;
                                                                opacity: 0.2;
                                                            }
                                                        }
                                                    `}</style>
                          <rect
                            className="spinner-bar"
                            fill="currentColor"
                            height="22"
                            width="6"
                            x="1"
                            y="1"
                          />
                          <rect
                            className="spinner-bar spinner-bars-2"
                            fill="currentColor"
                            height="22"
                            width="6"
                            x="9"
                            y="1"
                          />
                          <rect
                            className="spinner-bar spinner-bars-3"
                            fill="currentColor"
                            height="22"
                            width="6"
                            x="17"
                            y="1"
                          />
                        </svg>
                        <span className="text-sm text-gray-500">Thinking</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            </CardContent>

            {/* Input */}
            <div className="rounded-b-lg border-t bg-white p-4">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe your issue..."
                  className="flex-1 border-gray-200 focus-visible:ring-blue-500"
                  disabled={isLoading}
                />
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Ticket History Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-gray-500" />
                Ticket History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tickets.length === 0 ? (
                <EmptyState
                  icon={MessageCircle}
                  title="No support tickets yet"
                  description="Your support request history will appear here."
                />
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket: SupportTicket) => (
                    <div
                      key={ticket._id}
                      className="rounded-lg border p-3 transition-colors hover:border-blue-200 hover:bg-blue-50/50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2">
                          {getStatusIcon(ticket.status)}
                          <div>
                            <p className="line-clamp-1 text-sm font-medium text-gray-800">
                              {ticket.summary || "Support Request"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(ticket.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {getStatusBadge(ticket.status)}
                      </div>

                      {/* Show reply content when available */}
                      {ticket.replyContent && (
                        <div className="mt-3 border-t border-gray-100 pt-3">
                          <div className="mb-1.5 flex items-center gap-1.5">
                            <MessageCircle className="h-3.5 w-3.5 text-blue-500" />
                            <span className="text-xs font-medium text-blue-600">Support Reply</span>
                            {ticket.repliedAt && (
                              <span className="text-xs text-gray-400">
                                • {new Date(ticket.repliedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="rounded-md border-l-2 border-blue-400 bg-blue-50 p-2.5">
                            <p className="whitespace-pre-wrap text-sm text-gray-700">
                              {ticket.replyContent}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Help Links */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <HelpCircle className="h-5 w-5 text-gray-500" />
                Quick Help
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-600 hover:text-blue-600"
                onClick={() => setInput("How do I upgrade my subscription?")}
              >
                How do I upgrade my plan?
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-600 hover:text-blue-600"
                onClick={() => setInput("I need help with billing")}
              >
                Billing questions
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-600 hover:text-blue-600"
                onClick={() => setInput("Something isn't working correctly")}
              >
                Report a bug
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SupportPage;
